import type { ApiErrorBody } from "./types";

/**
 * Klient API.
 *
 * Uwierzytelnianie jest ciasteczkowe, więc każde żądanie idzie z `credentials`,
 * a każde zmieniające stan — z tokenem CSRF **odczytanym w chwili wysyłki**.
 * To nie jest ostrożność na zapas: Django rotuje token przy logowaniu, więc
 * wartość zapamiętana przy starcie aplikacji przestaje działać zaraz po tym,
 * jak użytkownik się zaloguje.
 */

const BAZA = "/api";

export class ApiError extends Error {
  status: number;
  body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(body.detail || `Błąd ${status}`);
    this.status = status;
    this.body = body;
  }

  /** Komunikaty pod konkretne pole formularza. */
  field(name: string): string | undefined {
    return this.body.fields?.[name]?.join(" ");
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isUnauthorized(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

function csrfToken(): string {
  const wpis = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith("csrftoken="));
  return wpis ? decodeURIComponent(wpis.slice("csrftoken=".length)) : "";
}

async function request<T>(
  path: string,
  { method = "GET", body }: { method?: string; body?: unknown } = {},
): Promise<T> {
  const naglowki: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) naglowki["Content-Type"] = "application/json";
  if (method !== "GET" && method !== "HEAD") naglowki["X-CSRFToken"] = csrfToken();

  const odpowiedz = await fetch(`${BAZA}${path}`, {
    method,
    credentials: "same-origin",
    headers: naglowki,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (odpowiedz.status === 204) return undefined as T;

  const tekst = await odpowiedz.text();
  const dane = tekst ? JSON.parse(tekst) : {};

  if (!odpowiedz.ok) {
    const blad: ApiErrorBody = dane.error ?? {
      code: "unknown",
      detail: `Błąd ${odpowiedz.status}`,
      fields: {},
      problems: [],
    };
    throw new ApiError(odpowiedz.status, blad);
  }
  return dane as T;
}

/** Wysyłka wieloczęściowa — dla dokumentów spoza KSeF, które trzeba załączyć.
 *  Nagłówka Content-Type nie ustawiamy: przeglądarka musi sama dopisać
 *  granicę części, inaczej serwer nie rozbierze żądania. */
async function requestForm<T>(path: string, form: FormData): Promise<T> {
  const odpowiedz = await fetch(`${BAZA}${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRFToken": csrfToken() },
    body: form,
  });
  const tekst = await odpowiedz.text();
  if (!odpowiedz.ok) {
    // Odpowiedź 413 pochodzi od odwrotnego proxy, nie od aplikacji, więc nie
    // ma kształtu `{"error": …}` — a komunikat „Błąd 413" nie mówi nikomu,
    // co zrobić z partią ze skanera.
    if (odpowiedz.status === 413) {
      throw new ApiError(413, {
        code: "too_large",
        detail: "Plik jest za duży dla serwera. Podziel partię na mniejsze "
          + "albo poproś administratora o podniesienie limitu wielkości "
          + "wgrywanych plików.",
        fields: {}, problems: [],
      });
    }
    let dane: { error?: ApiErrorBody } = {};
    try {
      dane = tekst ? JSON.parse(tekst) : {};
    } catch {
      /* Proxy zwróciło stronę błędu, nie JSON — zostaje sam kod stanu. */
    }
    throw new ApiError(odpowiedz.status, dane.error ?? {
      code: "unknown", detail: `Błąd ${odpowiedz.status}`, fields: {}, problems: [],
    });
  }
  return (tekst ? JSON.parse(tekst) : {}) as T;
}

/**
 * Pobranie pliku zamiast danych.
 *
 * Zwykły odsyłacz `href` byłby prostszy, ale składanie teczki dnia 1 trwa
 * kilkanaście sekund: użytkownik zostaje wtedy z pustą kartą i nie wie,
 * czy coś się dzieje. Przez `fetch` panel może pokazać oczekiwanie,
 * a po wszystkim — ile dokumentów naprawdę powstało.
 */
async function requestFile(
  path: string,
): Promise<{ blob: Blob; filename: string; headers: Headers }> {
  const odpowiedz = await fetch(`${BAZA}${path}`, {
    method: "GET",
    credentials: "same-origin",
    headers: { Accept: "application/pdf" },
  });

  if (!odpowiedz.ok) {
    const tekst = await odpowiedz.text();
    let dane: { error?: ApiErrorBody } = {};
    try {
      dane = tekst ? JSON.parse(tekst) : {};
    } catch {
      /* Serwer zwrócił stronę błędu, nie JSON — zostaje sam kod stanu. */
    }
    throw new ApiError(odpowiedz.status, dane.error ?? {
      code: "unknown", detail: `Błąd ${odpowiedz.status}`, fields: {}, problems: [],
    });
  }

  const dyspozycja = odpowiedz.headers.get("Content-Disposition") ?? "";
  const dopasowanie = /filename="([^"]+)"/.exec(dyspozycja);
  return {
    blob: await odpowiedz.blob(),
    filename: dopasowanie?.[1] ?? "dokument.pdf",
    headers: odpowiedz.headers,
  };
}

/** Zapis pliku na dysk użytkownika. */
export function zapiszPlik(blob: Blob, filename: string): void {
  const adres = URL.createObjectURL(blob);
  const odsylacz = document.createElement("a");
  odsylacz.href = adres;
  odsylacz.download = filename;
  document.body.appendChild(odsylacz);
  odsylacz.click();
  odsylacz.remove();
  // Zwolnienie po chwili: natychmiastowe potrafi wyprzedzić start pobierania.
  setTimeout(() => URL.revokeObjectURL(adres), 10_000);
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  postForm: requestForm,
  getFile: requestFile,
};
